// Centralized date/time formatting. All inputs are ISO timestamps (UTC); all
// outputs are rendered in the viewer's local timezone. Per spec §10.10:
// played_at is stored UTC and displayed in viewer-local time.

const pad = (n: number) => String(n).padStart(2, '0')

// Local-timezone "YYYY-MM-DD" — useful as a grouping key. Avoids the
// `iso.slice(0, 10)` trap, which gives the UTC date and groups plays into
// the wrong day at timezone boundaries.
export const localDayKey = (iso: string): string => {
  const d = new Date(iso)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// "Sat, Apr 25" (year omitted; for same-year-as-now use this short form).
export const formatDateShort = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })

// "Sat, Apr 25, 2026"
export const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

// "Saturday, Apr 25" — used as a per-day section header.
export const formatDayHeading = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  })

// "Apr 25, 2026" — date only, no weekday.
export const formatDateBare = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

// "8:49 AM"
export const formatTimeShort = (iso: string): string =>
  new Date(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })

// "Sat, Apr 25, 2026, 8:49 AM"
export const formatDateTime = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

// Relative-day labels for short summaries: "today" / "1d" / "3w" / "2mo".
export const formatRelativeShort = (iso: string): string => {
  const ms = Date.now() - new Date(iso).getTime()
  const days = Math.floor(ms / (1000 * 60 * 60 * 24))
  if (days === 0) return 'today'
  if (days === 1) return '1d'
  if (days < 7) return `${days}d`
  if (days < 30) return `${Math.floor(days / 7)}w`
  if (days < 365) return `${Math.floor(days / 30)}mo`
  return `${Math.floor(days / 365)}y`
}

// "today" / "yesterday" / "3d ago" / "2w ago" — verbose form for library rows.
export const formatRelativeLong = (iso: string | null): string => {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  const days = Math.floor(ms / (1000 * 60 * 60 * 24))
  if (days === 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 7) return `${days}d ago`
  if (days < 30) return `${Math.floor(days / 7)}w ago`
  if (days < 365) return `${Math.floor(days / 30)}mo ago`
  return `${Math.floor(days / 365)}y ago`
}
